/**
 * useExternalActions — Inteligência de Dados (Fase 4)
 * -----------------------------------------------------------------------------
 * Hooks das AÇÕES EM LOTE sobre os dados externos: criar contatos, clientes,
 * veículos, itens de lista de campanha ou tarefas a partir do resultado de um
 * filtro do Explorador ou de uma comparação com a base interna.
 *
 * Regras de arquitetura:
 *   - 100% via RPC SECURITY DEFINER (nada de acesso direto às tabelas).
 *   - O processamento é assíncrono (fila PGMQ + worker); a UI só acompanha.
 *   - A seleção é congelada (snapshot) no momento da confirmação.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-23 v1.0.0 - Criação inicial (Fase 4 - Ações em lote).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import type { DatasetFilter } from './useDatasetRecords';

const logger = createLogger('ExternalActions');

// =============================================================================
// TIPOS
// =============================================================================

/** Ações suportadas pelo worker (espelha a whitelist do backend) */
export type ExternalActionType =
  | 'create_contact'
  | 'create_customer'
  | 'create_vehicle'
  | 'add_to_campaign_list'
  | 'create_task';

/** Rótulos exibidos na UI */
export const EXTERNAL_ACTION_LABELS: Record<ExternalActionType, string> = {
  create_contact: 'Criar contatos',
  create_customer: 'Criar clientes',
  create_vehicle: 'Criar veículos',
  add_to_campaign_list: 'Adicionar em lista de campanha',
  create_task: 'Criar tarefas',
};

/** Seleção que originou o lote (filtro do explorador ou comparação) */
export interface ExternalActionSelection {
  mode: 'filter' | 'compare';
  search?: string | null;
  filters?: DatasetFilter[];
  target?: string;
  match_key?: string;
  match_path?: string | null;
  compare_mode?: 'MATCH' | 'NOT_MATCH';
}

/** Lote retornado pela listagem */
export interface ExternalActionBatch {
  id: string;
  dataset_id: string;
  dataset_name: string;
  action_type: ExternalActionType;
  name: string;
  status: string;
  total_items: number;
  processed: number;
  succeeded: number;
  failed: number;
  skipped: number;
  error: string | null;
  created_at: string;
  finished_at: string | null;
  total_count: number;
}

/** Item de um lote */
export interface ExternalActionItem {
  id: string;
  external_id: string | null;
  payload: Record<string, unknown>;
  status: string;
  entity_type: string | null;
  entity_id: string | null;
  error: string | null;
  processed_at: string | null;
  total_count: number;
}

// =============================================================================
// PRÉVIA DE IMPACTO
// =============================================================================

/** Quantos registros a seleção atinge (antes de confirmar a ação) */
export function useExternalActionPreview(options: {
  datasetId: string | null;
  selection: ExternalActionSelection;
  enabled?: boolean;
}) {
  const { datasetId, selection, enabled = true } = options;

  const previewQuery = useQuery({
    queryKey: ['external-action-preview', datasetId, selection],
    enabled: Boolean(enabled && datasetId),
    staleTime: 30 * 1000,
    queryFn: async (): Promise<number> => {
      const { data, error } = await supabase.rpc('preview_external_action_batch', {
        p_dataset_id: datasetId as string,
        p_selection: selection as never,
      });
      if (error) throw error;
      return Number(data ?? 0);
    },
  });

  return {
    total: previewQuery.data ?? 0,
    isLoading: previewQuery.isLoading || previewQuery.isFetching,
    error: previewQuery.error as Error | null,
  };
}

// =============================================================================
// LISTAGEM DE LOTES
// =============================================================================

/** Lotes de ação do tenant (opcionalmente filtrados por dataset) */
export function useExternalActionBatches(options?: {
  datasetId?: string | null;
  page?: number;
  pageSize?: number;
  /** Atualiza sozinho enquanto houver lote em andamento */
  autoRefresh?: boolean;
}) {
  const { datasetId = null, page = 0, pageSize = 20, autoRefresh = true } = options ?? {};

  const batchesQuery = useQuery({
    queryKey: ['external-action-batches', datasetId, page, pageSize],
    staleTime: 10 * 1000,
    refetchInterval: autoRefresh ? 15 * 1000 : false,
    queryFn: async (): Promise<ExternalActionBatch[]> => {
      const { data, error } = await supabase.rpc('get_external_action_batches', {
        p_dataset_id: datasetId,
        p_limit: pageSize,
        p_offset: page * pageSize,
      });
      if (error) {
        logger.error('Falha ao listar lotes de ação', { message: error.message });
        throw error;
      }
      return (data ?? []) as unknown as ExternalActionBatch[];
    },
  });

  const batches = batchesQuery.data ?? [];

  return {
    batches,
    totalCount: Number(batches[0]?.total_count ?? 0),
    isLoading: batchesQuery.isLoading,
    isFetching: batchesQuery.isFetching,
    error: batchesQuery.error as Error | null,
    refetch: batchesQuery.refetch,
  };
}

// =============================================================================
// ITENS DE UM LOTE
// =============================================================================

/** Itens de um lote (com filtro por situação) */
export function useExternalActionItems(options: {
  batchId: string | null;
  status?: string | null;
  page?: number;
  pageSize?: number;
}) {
  const { batchId, status = null, page = 0, pageSize = 50 } = options;

  const itemsQuery = useQuery({
    queryKey: ['external-action-items', batchId, status, page, pageSize],
    enabled: Boolean(batchId),
    staleTime: 10 * 1000,
    queryFn: async (): Promise<ExternalActionItem[]> => {
      const { data, error } = await supabase.rpc('get_external_action_items', {
        p_batch_id: batchId as string,
        p_status: status,
        p_limit: pageSize,
        p_offset: page * pageSize,
      });
      if (error) throw error;
      return (data ?? []) as unknown as ExternalActionItem[];
    },
  });

  const items = itemsQuery.data ?? [];

  return {
    items,
    totalCount: Number(items[0]?.total_count ?? 0),
    isLoading: itemsQuery.isLoading,
  };
}

// =============================================================================
// MUTAÇÕES
// =============================================================================

/** Criação, cancelamento e reprocessamento de lotes */
export function useExternalActionMutations() {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['external-action-batches'] });
    queryClient.invalidateQueries({ queryKey: ['external-action-items'] });
  };

  const createBatch = useMutation({
    mutationFn: async (input: {
      datasetId: string;
      actionType: ExternalActionType;
      name: string;
      config: Record<string, unknown>;
      selection: ExternalActionSelection;
    }): Promise<string> => {
      const { data, error } = await supabase.rpc('create_external_action_batch', {
        p_dataset_id: input.datasetId,
        p_action_type: input.actionType,
        p_name: input.name,
        p_config: input.config as never,
        p_selection: input.selection as never,
      });
      if (error) throw error;
      return data as unknown as string;
    },
    onSuccess: invalidate,
  });

  const cancelBatch = useMutation({
    mutationFn: async (batchId: string) => {
      const { error } = await supabase.rpc('cancel_external_action_batch', {
        p_batch_id: batchId,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const retryBatch = useMutation({
    mutationFn: async (batchId: string): Promise<number> => {
      const { data, error } = await supabase.rpc('retry_external_action_batch', {
        p_batch_id: batchId,
      });
      if (error) throw error;
      return Number(data ?? 0);
    },
    onSuccess: invalidate,
  });

  return { createBatch, cancelBatch, retryBatch };
}
